import React from 'react';
import Hero from '../components/Hero';
import StandardSection from '../components/StandardSection';
import InnovativeMaterial from '../components/InnovativeMaterial';
import ProductLine from '../components/ProductLine';
import Applications from '../components/Applications';
import OurProjects from '../components/OurProjects';
import Services from '../components/Services';
import Clients from '../components/Clients';
import HomeCTA from '../components/HomeCTA';
import SEOHead, { seoConfig } from '../components/SEOHead';
import { useLanguage } from '../context/LanguageContext';
import { API_URL } from '../config';



const quoteContent = {
  ru: {
    quote: '"ГИПСОВИНИЛ — ЭТО ИНЖЕНЕРНОЕ РЕШЕНИЕ С ПРОДУМАННЫМ ДИЗАЙНОМ И БЕЗУПРЕЧНЫМ СТИЛЕМ, КОТОРОЕ ПРИДАЕТ СТАТУСНЫЙ ВИД ПРОСТРАНСТВУ. ОНО УНИКАЛЬНО, ПОТОМУ ЧТО СДЕЛАНО В КАЗАХСТАНЕ — ДЛЯ КАЗАХСТАНА."',
    author: '— Основатель проекта "Гипсовинил"'
  },
  kz: {
    quote: '"ГИПСОВИНИЛ — БҰЛ ОЙЛАСТЫРЫЛҒАН ДИЗАЙНЫ МЕН МІНСІЗ СТИЛІ БАР ИНЖЕНЕРЛІК ШЕШІМ. ОЛ КЕҢІСТІККЕ МӘРТЕБЕЛІ КӨРІНІС БЕРЕДІ. ОЛ БІРЕГЕЙ, ӨЙТКЕНІ ҚАЗАҚСТАНДА ЖАСАЛҒАН — ҚАЗАҚСТАН ҮШІН."',
    author: '— "Гипсовинил" жобасының негізін қалаушы'
  },
  en: {
    quote: '"GIPSOVINIL IS AN ENGINEERING SOLUTION WITH THOUGHTFUL DESIGN AND IMPECCABLE STYLE THAT GIVES A PRESTIGIOUS LOOK TO ANY SPACE. IT IS UNIQUE BECAUSE IT IS MADE IN KAZAKHSTAN — FOR KAZAKHSTAN."',
    author: '— Founder of Gipsovinil Project'
  },
  ar: {
    quote: '"جيبسوفينيل حلٌّ هندسيٌّ بتصميم مدروس وأسلوب لا تشوبه شائبة، يمنح الفضاء طابعاً مرموقاً. وهو فريدٌ لأنه صُنع في كازاخستان — من أجل كازاخستان."',
    author: '— مؤسس مشروع «جيبسوفينيل»'
  }
};

const benefitsContent = {
  ru: [
    { bold: 'РАЗНООБРАЗИЕ ОСНОВ', text: '— для каждого проекта и технического задания' },
    { bold: 'МНОГОФУНКЦИОНАЛЬНАЯ СБОРКА И РАЗБОРКА ПАНЕЛЕЙ', text: '— фрагментальная замена панелей и постоянный доступ к коммуникациям' },
    { bold: 'ВОЗМОЖНОСТЬ МОДУЛЬНОЙ СБОРКИ И ПЕРЕСТРОЙКИ ПЕРЕГОРОДОК', text: '— гибкий интерьер' },
    { bold: 'ВЫСОТА ПАНЕЛЕЙ ДО 3 М', text: '— минимум стыков, максимум эстетики' },
    { bold: 'ЭКСКЛЮЗИВНАЯ АВТОРСКАЯ КОЛЛЕКЦИЯ ЦВЕТОВ,', text: ' адаптированная под восточные вкусы' },
    { bold: 'ЛИНЕЙКА ПО СЕГМЕНТАМ:', text: ' от Бюджета до Luxe — для разных задач и бюджетов' }
  ],
  kz: [
    { bold: 'НЕГІЗДЕРДІҢ АЛУАН ТҮРЛІЛІГІ', text: '— әр жоба мен техникалық тапсырма үшін' },
    { bold: 'КӨПФУНКЦИОНАЛДЫ ҚҰРАСТЫРУ ЖӘНЕ БӨЛШЕКТЕУ', text: '— панельдерді фрагменттік ауыстыру және коммуникацияларға тұрақты қол жеткізу' },
    { bold: 'МОДУЛЬДІК ҚҰРАСТЫРУ ЖӘНЕ ҚАЙТА ЖОСПАРЛАУ МҮМКІНДІГІ', text: '— икемді интерьер' },
    { bold: 'ПАНЕЛЬДЕРДІҢ БИІКТІГІ 3 М ДЕЙІН', text: '— минимум жіктер, максимум эстетика' },
    { bold: 'ЭКСКЛЮЗИВТІК АВТОРЛЫҚ ТҮСТЕР КОЛЛЕКЦИЯСЫ,', text: ' шығыс талғамына бейімделген' },
    { bold: 'СЕГМЕНТТЕР БОЙЫНША ЖЕЛІ:', text: ' Бюджеттен Luxe-ке дейін — түрлі міндеттер мен бюджеттер үшін' }
  ],
  en: [
    { bold: 'VARIETY OF BASES', text: '— for every project and technical requirement' },
    { bold: 'MULTIFUNCTIONAL ASSEMBLY AND DISASSEMBLY', text: '— panel replacement and constant access to utilities' },
    { bold: 'MODULAR ASSEMBLY AND PARTITION RECONFIGURATION', text: '— flexible interior' },
    { bold: 'PANEL HEIGHT UP TO 3 M', text: '— minimum joints, maximum aesthetics' },
    { bold: 'EXCLUSIVE AUTHOR COLOR COLLECTION,', text: ' adapted to Eastern tastes' },
    { bold: 'PRODUCT LINE BY SEGMENTS:', text: ' from Budget to Luxe — for different tasks and budgets' }
  ],
  ar: [
    { bold: 'تنوّع في القواعد', text: ' — لكل مشروع ومواصفات فنية' },
    { bold: 'تجميع وتفكيك متعدد الوظائف للألواح', text: ' — استبدال جزئي للألواح ووصول دائم للتمديدات' },
    { bold: 'إمكانية التجميع النمطي وإعادة تشكيل القواطع', text: ' — تصميم داخلي مرن' },
    { bold: 'ارتفاع الألواح حتى 3 أمتار', text: ' — أقل عدد من الوصلات وأعلى قدر من الجمال' },
    { bold: 'مجموعة ألوان حصرية من تصميم مبتكرينا،', text: ' مُكيّفة لأذواق الشرق' },
    { bold: 'خط منتجات حسب الشرائح:', text: ' من الاقتصادي إلى الفاخر — لمختلف المهام والميزانيات' }
  ]
};

const HomePage = () => {
  const { language } = useLanguage();
  const seo = seoConfig.home[language] || seoConfig.home.en || seoConfig.home.ru;
  const q = quoteContent[language] || quoteContent.ru;
  const benefits = benefitsContent[language] || benefitsContent.ru;

  return (
    <>
      <SEOHead 
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
      />
      <Hero />
      <section className="bg-[#F5F5F0] py-14" data-testid="quote-section">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <p className="text-[#061d36] text-lg md:text-xl leading-relaxed font-medium italic mb-6">{q.quote}</p>
          <p className="text-[#061d36]/50 text-base">{q.author}</p>
        </div>
      </section>
      <section className="bg-[#F5F5F0] pb-14" data-testid="benefits-section">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex flex-col md:flex-row gap-10">
            <div className="flex-1 space-y-6">
              {benefits.map((b, i) => (
                <div key={i} className="border-l-2 border-[#8b4444] pl-5">
                  <p className="text-[#061d36]">
                    <span className="font-bold">{b.bold}</span>
                    <span className="text-[#061d36]/60">{b.text}</span>
                  </p>
                </div>
              ))}
            </div>
            <div className="w-full md:w-[480px] flex-shrink-0">
              <div className="w-full flex items-center justify-center" data-testid="benefits-photo">
                <img src="/uploads/panel-layers.png" alt="Состав панели" className="w-full max-w-[440px] h-auto object-contain drop-shadow-2xl" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>
      <ProductLine />
      <InnovativeMaterial />
      <Applications />
      <StandardSection />
      <Services />
      <OurProjects />
      <Clients />
      <HomeCTA />
    </>
  );
};

export default HomePage;
